/* =============================================================
   BentoKids — Advanced Header JS
   - Live Predictive Search (Shopify API)
   - Mobile Nav Drawer
   - Transparent Header Scroll Watcher
   ============================================================= */
(function () {
  'use strict';

  /* -----------------------------------------------------------
     UTILITIES
  ----------------------------------------------------------- */
  function esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function money(cents) {
    if (cents == null) return '';
    return window.Shopify && window.Shopify.formatMoney
      ? window.Shopify.formatMoney(cents)
      : 'Rs.' + (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* -----------------------------------------------------------
     PREDICTIVE SEARCH
  ----------------------------------------------------------- */
  var BKSearch = {
    DEBOUNCE_MS: 280,
    MAX_RECENT: 6,
    _timer: null,
    _xhr: null,

    init: function () {
      this.overlay = document.getElementById('bk-search-overlay');
      this.input = document.getElementById('bk-search-input');
      this.resultBox = document.getElementById('bk-search-results');
      this.spinner = document.getElementById('bk-search-spinner');
      this.clearBtn = document.getElementById('bk-search-clear');
      this.defaultEl = document.getElementById('bk-search-default');
      this.footerEl = document.getElementById('bk-search-footer');

      if (!this.overlay || !this.input) return;

      this._renderRecentTags();
      this._bindEvents();
    },

    open: function () {
      this.overlay.setAttribute('aria-hidden', 'false');
      this.overlay.classList.add('is-open');
      document.body.classList.add('bk-no-scroll');
      var self = this;
      setTimeout(function () { self.input.focus(); }, 60);
    },

    close: function () {
      this.overlay.setAttribute('aria-hidden', 'true');
      this.overlay.classList.remove('is-open');
      document.body.classList.remove('bk-no-scroll');
    },

    _bindEvents: function () {
      var self = this;

      /* Open triggers */
      document.querySelectorAll('[data-bk-search-open]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          self.open();
        });
      });

      /* Overlay backdrop click */
      this.overlay.addEventListener('click', function (e) {
        if (e.target === self.overlay) self.close();
      });

      /* Close button */
      var closeBtn = this.overlay.querySelector('.bk-search__close-btn');
      if (closeBtn) closeBtn.addEventListener('click', function () { self.close(); });

      /* ESC key */
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.close();
      });

      /* Input — live search */
      this.input.addEventListener('input', function () {
        var q = self.input.value.trim();
        self._toggleClearBtn(q.length > 0);
        clearTimeout(self._timer);
        if (q.length < 2) { self._showDefault(); return; }
        self._timer = setTimeout(function () { self._fetch(q); }, self.DEBOUNCE_MS);
      });

      /* Clear button */
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', function () {
          self.input.value = '';
          self._toggleClearBtn(false);
          self._showDefault();
          self.input.focus();
        });
      }

      /* Keyboard navigation */
      this.input.addEventListener('keydown', function (e) {
        self._handleKeydown(e);
      });

      /* Tag clicks (recent / popular) */
      this.overlay.addEventListener('click', function (e) {
        var tag = e.target.closest('.bk-search__tag');
        if (!tag) return;
        var q = tag.dataset.query || tag.textContent.trim().replace(/^[^\w]+/, '');
        self.input.value = q;
        self._toggleClearBtn(true);
        clearTimeout(self._timer);
        self._fetch(q);
      });
    },

    _toggleClearBtn: function (show) {
      if (!this.clearBtn) return;
      this.clearBtn.style.display = show ? 'flex' : 'none';
    },

    _showDefault: function () {
      if (this.resultBox) this.resultBox.innerHTML = '';
      if (this.defaultEl) this.defaultEl.style.display = '';
      if (this.footerEl) this.footerEl.style.display = 'none';
      this._setSpinner(false);
    },

    _setSpinner: function (on) {
      if (this.spinner) this.spinner.style.display = on ? 'flex' : 'none';
      if (this.defaultEl && on) this.defaultEl.style.display = 'none';
    },

    _fetch: function (q) {
      var self = this;

      /* Abort in-flight request */
      if (this._xhr) { this._xhr.abort(); }

      this._setSpinner(true);

      var url = '/search/suggest.json'
        + '?q=' + encodeURIComponent(q)
        + '&resources[type]=product,collection,article,page'
        + '&resources[limit]=5'
        + '&resources[options][fields]=title,product_type,variants.title';

      var xhr = new XMLHttpRequest();
      this._xhr = xhr;
      xhr.open('GET', url);
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      xhr.onload = function () {
        if (xhr.status === 200) {
          try {
            var data = JSON.parse(xhr.responseText);
            self._render(data.resources.results, q);
          } catch (err) {
            self._renderError();
          }
        } else {
          self._renderError();
        }
      };
      xhr.onerror = function () { self._renderError(); };
      xhr.send();
    },

    _render: function (results, q) {
      this._setSpinner(false);
      var products = results.products || [];
      var collections = results.collections || [];
      var articles = results.articles || [];
      var pages = results.pages || [];
      var total = products.length + collections.length + articles.length + pages.length;

      if (total === 0) {
        if (this.resultBox) {
          this.resultBox.innerHTML =
            '<p class="bk-search__empty">No results for &ldquo;' + esc(q) + '&rdquo;. Try a different search.</p>';
        }
        if (this.footerEl) this.footerEl.style.display = 'none';
        return;
      }

      var html = '';

      /* Products */
      if (products.length) {
        html += '<div class="bk-search__group"><p class="bk-search__group-label">Products</p>';
        products.forEach(function (p) {

          var imgHtml = p.image
            ? '<img class="bk-search__item-img" src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" width="56" height="56">'
            : '<div class="bk-search__item-img bk-search__item-img--placeholder"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="22" height="22"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg></div>';
          var priceHtml = p.price != null
            ? '<span class="bk-search__item-price">' + money(p.price) + '</span>'
            : '';
          var compareHtml = p.compare_at_price && p.compare_at_price > p.price
            ? '<span class="bk-search__item-compare">' + money(p.compare_at_price) + '</span>'
            : '';
          html += '<a href="' + esc(p.url) + '" class="bk-search__item" data-bk-result tabindex="0">'
            + imgHtml
            + '<div class="bk-search__item-body">'
            + '<span class="bk-search__item-title">' + esc(p.title) + '</span>'
            + (p.product_type ? '<span class="bk-search__item-meta">' + esc(p.product_type) + '</span>' : '')
            + '</div>'
            + '<div class="bk-search__item-price-wrap">' + priceHtml + compareHtml + '</div>'
            + '</a>';
        });
        html += '</div>';
      }

      /* Collections */
      if (collections.length) {
        html += '<div class="bk-search__group"><p class="bk-search__group-label">Collections</p>';
        collections.forEach(function (c) {
          html += '<a href="' + esc(c.url) + '" class="bk-search__item" data-bk-result tabindex="0">'
            + '<div class="bk-search__item-img bk-search__item-img--placeholder"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="22" height="22"><path d="M3 3h7v7H3zm11 0h7v7h-7zM3 14h7v7H3zm11 0h7v7h-7z"/></svg></div>'
            + '<div class="bk-search__item-body"><span class="bk-search__item-title">' + esc(c.title) + '</span><span class="bk-search__item-meta">Collection</span></div>'
            + '</a>';
        });
        html += '</div>';
      }

      /* Articles */
      if (articles.length) {
        html += '<div class="bk-search__group"><p class="bk-search__group-label">Articles</p>';
        articles.forEach(function (a) {
          html += '<a href="' + esc(a.url) + '" class="bk-search__item" data-bk-result tabindex="0">'
            + '<div class="bk-search__item-img bk-search__item-img--placeholder"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="22" height="22"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></div>'
            + '<div class="bk-search__item-body"><span class="bk-search__item-title">' + esc(a.title) + '</span><span class="bk-search__item-meta">Article</span></div>'
            + '</a>';
        });
        html += '</div>';
      }

      /* Pages */
      if (pages.length) {
        html += '<div class="bk-search__group"><p class="bk-search__group-label">Pages</p>';
        pages.forEach(function (pg) {
          html += '<a href="' + esc(pg.url) + '" class="bk-search__item" data-bk-result tabindex="0">'
            + '<div class="bk-search__item-img bk-search__item-img--placeholder"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="22" height="22"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 10h8M8 14h5"/></svg></div>'
            + '<div class="bk-search__item-body"><span class="bk-search__item-title">' + esc(pg.title) + '</span><span class="bk-search__item-meta">Page</span></div>'
            + '</a>';
        });
        html += '</div>';
      }

      if (this.resultBox) this.resultBox.innerHTML = html;
      if (this.footerEl) {
        this.footerEl.style.display = '';
        var link = this.footerEl.querySelector('a');
        if (link) link.href = '/search?q=' + encodeURIComponent(q);
      }

      this._saveRecent(q);
    },

    _renderError: function () {
      this._setSpinner(false);
      if (this.resultBox) {
        this.resultBox.innerHTML = '<p class="bk-search__empty">Something went wrong. Please try again.</p>';
      }
    },

    _handleKeydown: function (e) {
      if (!this.resultBox) return;
      var items = Array.from(this.resultBox.querySelectorAll('[data-bk-result]'));
      var focused = this.resultBox.querySelector('.is-focused');
      var idx = focused ? items.indexOf(focused) : -1;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (focused) focused.classList.remove('is-focused');
        var next = items[idx + 1] || items[0];
        if (next) { next.classList.add('is-focused'); next.scrollIntoView({ block: 'nearest' }); }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (focused) focused.classList.remove('is-focused');
        var prev = items[idx - 1] || items[items.length - 1];
        if (prev) { prev.classList.add('is-focused'); prev.scrollIntoView({ block: 'nearest' }); }
      } else if (e.key === 'Enter' && focused) {
        e.preventDefault();
        this._saveRecent(this.input.value.trim());
        window.location.href = focused.href;
      }
    },

    /* Recent searches */
    _getRecent: function () {
      try { return JSON.parse(localStorage.getItem('bk_searches') || '[]'); } catch (e) { return []; }
    },
    _saveRecent: function (q) {
      if (!q || q.length < 2) return;
      var arr = this._getRecent().filter(function (x) { return x !== q; });
      arr.unshift(q);
      try { localStorage.setItem('bk_searches', JSON.stringify(arr.slice(0, this.MAX_RECENT))); } catch (e) { }
      this._renderRecentTags();
    },
    _renderRecentTags: function () {
      var wrap = document.getElementById('bk-search-recent-wrap');
      var list = document.getElementById('bk-search-recent-list');
      if (!list) return;
      var arr = this._getRecent();
      if (!arr.length) { if (wrap) wrap.style.display = 'none'; return; }
      if (wrap) wrap.style.display = '';
      list.innerHTML = arr.map(function (q) {
        return '<button class="bk-search__tag" data-query="' + esc(q) + '" type="button">'
          + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="13" height="13"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>'
          + esc(q) + '</button>';
      }).join('');
    }
  };

  /* -----------------------------------------------------------
     MOBILE NAV DRAWER
  ----------------------------------------------------------- */
  var BKMobileNav = {
    init: function () {
      var drawer = document.getElementById('bk-mobile-drawer');
      if (!drawer) return;
      var overlay = drawer.querySelector('.bk-mobile-drawer__overlay');
      var closeBtn = drawer.querySelector('.bk-mobile-drawer__close');

      function open() {
        drawer.classList.add('is-open');
        document.body.classList.add('bk-no-scroll');
        drawer.setAttribute('aria-hidden', 'false');
      }
      function close() {
        drawer.classList.remove('is-open');
        document.body.classList.remove('bk-no-scroll');
        drawer.setAttribute('aria-hidden', 'true');
      }

      document.querySelectorAll('[data-bk-nav-open]').forEach(function (btn) {
        btn.addEventListener('click', open);
      });
      if (overlay) overlay.addEventListener('click', close);
      if (closeBtn) closeBtn.addEventListener('click', close);
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });

      /* Sub-menu accordions */
      drawer.querySelectorAll('.bk-mobile-nav__toggle').forEach(function (toggle) {
        toggle.addEventListener('click', function () {
          var sub = document.getElementById(toggle.getAttribute('aria-controls'));
          var expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', !expanded);
          if (sub) sub.classList.toggle('is-open', !expanded);
        });
      });
    }
  };

  /* -----------------------------------------------------------
     TRANSPARENT HEADER
  ----------------------------------------------------------- */
  var BKHeaderScroll = {
    init: function () {
      var header = document.querySelector('.bk-header--transparent');
      if (!header) return;
      function update() {
        header.classList.toggle('bk-header--scrolled', window.scrollY > 60);
      }
      window.addEventListener('scroll', update, { passive: true });
      update();
    }
  };

  /* -----------------------------------------------------------
     INIT
  ----------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', function () {
    BKSearch.init();
    BKMobileNav.init();
    BKHeaderScroll.init();
  });

  window.BKSearch = BKSearch;

}());